import BreadCrumb from "Common/BreadCrumb";
import TableContainer from "Common/TableContainer";
import Swal from "sweetalert2";

// Icons
import { Search, Download, SlidersHorizontal, Trash2 } from "lucide-react";

import React, { useState, useEffect } from "react";
import UserCreateModal from "./components/Modal/Create";
import UsersData from "./components/tabledata";
import { useAppServices } from "hook/services";
import TableSkeleton from "Common/Components/Loader/TableSkeleton";

const Users = () => {
  const AppService = useAppServices();
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const {
    loading,
    columns,
    users,
    selectedRows,
    pagination,
    searchTerm,
    handleRefresh,
    handleSearch,
    handlePageChange,
    handlePageSizeChange,
    handleSort,
    EditModel,
    resetSelectedRows,
    locations,
  } = UsersData();

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch(debouncedSearch);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [debouncedSearch]);

  const handleBulkAction = async (type: "delete" | "update") => {
    if (selectedRows.length === 0) return;

    if (type === "delete") {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: `You are about to delete ${selectedRows.length} User(s). This action cannot be undone.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ef4444",
        cancelButtonColor: "#6b7280",
        confirmButtonText: "Yes, Delete!",
      });

      if (!result.isConfirmed) return;
    }

    try {
      const payload = {
        type,
        selectedIds: selectedRows,
      };
      const { response, error } = await AppService.user.bulk_action({
        payload,
        toaster: true,
      });

      if (error) throw new Error(error.message || "Bulk action failed");

      handleRefresh();
      resetSelectedRows();
    } catch (err: any) {
      console.error("Bulk action error:", err);
    }
  };

  return (
    <React.Fragment>
      <BreadCrumb title="List" pageTitle="Users" />
      {EditModel}
      <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-12">
        <div className="xl:col-span-12">
          <div className="card" id="userTable">
            <div className="card-body">
              <div className="flex items-center">
                <h6 className="text-15 grow">User List</h6>
                <div className="shrink-0">
                  <UserCreateModal handleRefresh={handleRefresh} locations={locations} />
                </div>
              </div>
              {selectedRows.length > 0 && (
                <div className="mt-2 mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 dark:border-zink-600 dark:bg-zink-700/30">
                  <span className="text-sm font-medium text-slate-700 dark:text-zink-200">
                    {selectedRows.length} item(s) selected
                  </span>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => handleBulkAction("delete")}
                      className="inline-flex items-center rounded-md bg-red-500 px-3 py-2 text-sm font-medium text-white shadow hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-300 disabled:opacity-50"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="!py-3.5 card-body border-y border-dashed border-slate-200 dark:border-zink-500">
              <form onSubmit={(e) => e.preventDefault()}>
                <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
                  <div className="relative xl:col-span-2">
                    <input
                      type="text"
                      className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      placeholder="Search for name, email, phone number etc..."
                      autoComplete="off"
                      value={debouncedSearch}
                      onChange={(e) => setDebouncedSearch(e.target.value)}
                    />
                    <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
                  </div>
                </div>
              </form>
            </div>
            {loading ? (
              <TableSkeleton />
            ) : (
              <div className="card-body">
                  <TableContainer
                    isPagination={true}
                    columns={columns || []}
                    data={users || []}
                    customPageSize={pagination.limit}
                    currentPage={pagination.page - 1} // Convert to 0-based for TableContainer
                    totalCount={pagination.total}
                    totalPages={pagination.pages}
                    onPageChange={(pageIndex) => handlePageChange(pageIndex + 1)} // Convert back to 1-based
                    onPageSizeChange={handlePageSizeChange}
                    sortField={pagination.sortField}
                    sortOrder={pagination.sortOrder}
                    onSort={handleSort}
                    divclassName="-mx-5 -mb-5 overflow-x-auto"
                    tableclassName="w-full border-separate table-custom border-spacing-y-1 whitespace-nowrap"
                    theadclassName="text-left relative rounded-md bg-slate-100 dark:bg-zink-600 after:absolute ltr:after:border-l-2 rtl:after:border-r-2 ltr:after:left-0 rtl:after:right-0 after:top-0 after:bottom-0 after:border-transparent [&.active]:after:border-custom-500 [&.active]:bg-slate-100 dark:[&.active]:bg-zink-600"
                    thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold"
                    tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5"
                    PaginationClassName="flex flex-col items-center mt-8 md:flex-row"
                  />
              </div>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Users;